import Head from 'next/head';

import {
  buildEventUrl,
  buildShareDescription,
  eventHasEnded,
  isEventHidden,
  resolveCanonicalEventId,
  resolveShareImage,
  resolveShareTitle,
  SHARE_IMAGE_HEIGHT,
  SHARE_IMAGE_WIDTH,
} from '@utils/eventMeta';
import { buildEventSchema, toJsonLd } from '@utils/eventSchema';

interface IEventMetaProperties {
  event: IEvent;
  events?: IEvent[];
  eventSeries?: IEventSeries;
  venue?: IVenue;
  tickets?: (ITicket | ITicketSummary)[];
  talents?: ITalent[];
}

/**
 * Link preview metadata for an event, rendered from the server-side props so
 * that it reaches crawlers, which do not run JavaScript.
 */
export const EventMeta = ({
  event,
  events,
  eventSeries,
  venue,
  tickets,
  talents,
}: IEventMetaProperties) => {
  const title = resolveShareTitle(event, eventSeries);
  const description = buildShareDescription({
    event,
    events,
    eventSeries,
    venue,
  });
  const url = buildEventUrl(
    resolveCanonicalEventId(event, events, eventSeries)
  );
  const image = resolveShareImage(event, eventSeries);
  const schema = buildEventSchema({
    event,
    events,
    eventSeries,
    venue,
    tickets,
    talents,
  });

  return (
    <Head>
      <title>{`${title} | Scope Tickets`}</title>
      <link rel='canonical' href={url} />
      {description ? <meta name='description' content={description} /> : null}
      {/* Hidden events are unlisted: keep crawlers off the page and off
          everything it links to. Finished ones only need de-indexing. */}
      {isEventHidden(event) ? (
        <meta name='robots' content='noindex, nofollow' />
      ) : eventHasEnded(event, events) ? (
        <meta name='robots' content='noindex, follow' />
      ) : null}

      <meta property='og:type' content='website' />
      <meta property='og:site_name' content='Scope Tickets' />
      <meta property='og:title' content={title} />
      <meta property='og:url' content={url} />
      {description ? (
        <meta property='og:description' content={description} />
      ) : null}
      {image ? <meta property='og:image' content={image} /> : null}
      {image ? (
        <meta property='og:image:width' content={String(SHARE_IMAGE_WIDTH)} />
      ) : null}
      {image ? (
        <meta property='og:image:height' content={String(SHARE_IMAGE_HEIGHT)} />
      ) : null}
      {image ? <meta property='og:image:alt' content={title} /> : null}

      <meta name='twitter:card' content='summary_large_image' />
      {image ? <meta name='twitter:image:alt' content={title} /> : null}

      {schema ? (
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{ __html: toJsonLd(schema) }}
        />
      ) : null}
    </Head>
  );
};
